/**
 * Keyboard.ts — keyboard input source for the InputManager.
 *
 * Returns RAW screen-space intent: `steer` is +1 for "press right" / -1 for
 * "press left". The §6.2 screen-relative flip is applied later, only for the
 * player. The single use-item control (M12: Space, Shift, or E — all fire the
 * one held item) is reported as a HELD boolean; the InputManager turns it into
 * a one-shot "fire" edge so holding a key fires exactly once.
 */
export interface KeyboardReading {
  throttle: number; // +1 forward, -1 back
  steer: number; // +1 right, -1 left (raw, screen-space)
  useItem: boolean; // Space / Shift / E held — fire the held item
  pause: boolean; // Escape or P held
  active: boolean;
}

const DRIVE_KEYS = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'Space', // use-item — must not scroll the page
];

export class Keyboard {
  private keys = new Set<string>();

  constructor() {
    window.addEventListener('keydown', this.onDown);
    window.addEventListener('keyup', this.onUp);
  }

  private onDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
    if (DRIVE_KEYS.includes(e.code)) e.preventDefault();
  };

  private onUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  read(): KeyboardReading {
    const k = this.keys;
    const up = k.has('ArrowUp') || k.has('KeyW');
    const down = k.has('ArrowDown') || k.has('KeyS');
    const left = k.has('ArrowLeft') || k.has('KeyA');
    const right = k.has('ArrowRight') || k.has('KeyD');
    return {
      throttle: (up ? 1 : 0) - (down ? 1 : 0),
      steer: (right ? 1 : 0) - (left ? 1 : 0),
      useItem: k.has('Space') || k.has('ShiftLeft') || k.has('ShiftRight') || k.has('KeyE'),
      pause: k.has('Escape') || k.has('KeyP'),
      active: up || down || left || right,
    };
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onDown);
    window.removeEventListener('keyup', this.onUp);
  }
}
